import React from 'react';
import { Plus, Trash, Users, MapPin, BookOpen, Clock } from '@phosphor-icons/react';
import EnhancedRichTextEditor from '../../EnhancedRichTextEditor';

export default function StoryModule({ metadata, onChange }) {
  const handleChange = (field, value) => {
    onChange({ ...metadata, [field]: value });
  };

  const addCharacter = () => {
    const newCharacters = [...(metadata.characters || []), { name: '', role: '' }];
    handleChange('characters', newCharacters);
  };

  const updateCharacter = (index, field, value) => {
    const newCharacters = [...(metadata.characters || [])];
    newCharacters[index] = { ...newCharacters[index], [field]: value };
    handleChange('characters', newCharacters);
  };

  const removeCharacter = (index) => {
    const newCharacters = (metadata.characters || []).filter((_, i) => i !== index);
    handleChange('characters', newCharacters);
  };

  return (
    <div className="space-y-6">
      {/* Story Content */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <BookOpen size={16} />
          Story Content <span className="text-red-500">*</span>
        </label>
        <EnhancedRichTextEditor
          value={metadata.story_content || ''}
          onChange={(html) => handleChange('story_content', html)}
          placeholder="Write your story here..."
        />
      </div>

      {/* Summary */}
      <div>
        <label className="block text-sm font-medium mb-2">
          Summary / Synopsis
        </label>
        <textarea
          value={metadata.summary || ''}
          onChange={(e) => handleChange('summary', e.target.value)}
          placeholder="Brief summary of your story..."
          className="w-full px-4 py-3 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors resize-none"
          rows={4}
        />
      </div>

      {/* Characters */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium flex items-center gap-2">
            <Users size={16} />
            Characters
          </label>
          <button
            onClick={addCharacter}
            className="flex items-center gap-1 px-3 py-1 text-sm rounded-lg bg-primary text-primary-foreground hover:opacity-90"
          >
            <Plus size={14} weight="bold" />
            Add Character
          </button>
        </div>

        {(metadata.characters || []).length === 0 && (
          <button
            onClick={addCharacter}
            className="w-full p-6 border-2 border-dashed border-border rounded-lg hover:border-primary transition-colors"
          >
            <div className="text-center">
              <Users size={24} className="mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Add characters to your story</p>
            </div>
          </button>
        )}

        <div className="space-y-3">
          {(metadata.characters || []).map((character, index) => (
            <div key={index} className="flex gap-3 items-start group">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  type="text"
                  value={character.name || ''}
                  onChange={(e) => updateCharacter(index, 'name', e.target.value)}
                  placeholder="Character name"
                  className="px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
                />
                <input
                  type="text"
                  value={character.role || ''}
                  onChange={(e) => updateCharacter(index, 'role', e.target.value)}
                  placeholder="Role (e.g., Protagonist, Antagonist)"
                  className="px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
                />
              </div>
              <button
                onClick={() => removeCharacter(index)}
                className="p-2 rounded-lg text-red-500 opacity-0 group-hover:opacity-100 hover:bg-red-50 dark:hover:bg-red-950/30 transition-all"
              >
                <Trash size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Setting */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <MapPin size={16} />
          Setting
        </label>
        <input
          type="text"
          value={metadata.setting || ''}
          onChange={(e) => handleChange('setting', e.target.value)}
          placeholder="Where does the story take place?"
          className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
        />
      </div>

      {/* Genre & Theme */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            Genre
          </label>
          <select
            value={metadata.genre || ''}
            onChange={(e) => handleChange('genre', e.target.value)}
            className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
          >
            <option value="">Select genre...</option>
            <option value="Fiction">Fiction</option>
            <option value="Fantasy">Fantasy</option>
            <option value="Science Fiction">Science Fiction</option>
            <option value="Mystery">Mystery</option>
            <option value="Thriller">Thriller</option>
            <option value="Romance">Romance</option>
            <option value="Horror">Horror</option>
            <option value="Historical">Historical</option>
            <option value="Contemporary">Contemporary</option>
            <option value="Magical Realism">Magical Realism</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">
            Theme
          </label>
          <input
            type="text"
            value={metadata.theme || ''}
            onChange={(e) => handleChange('theme', e.target.value)}
            placeholder="e.g., Coming of age, Redemption"
            className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
          />
        </div>
      </div>

      {/* Reading Time */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <Clock size={16} />
          Estimated Reading Time (minutes)
        </label>
        <input
          type="number"
          value={metadata.reading_time || ''}
          onChange={(e) => handleChange('reading_time', parseInt(e.target.value) || 0)}
          placeholder="e.g., 5"
          min="1"
          className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Leave empty to auto-calculate based on word count
        </p>
      </div>
    </div>
  );
}
